import { UserAutocomplete } from '@/features/participant';
import { BackButton } from '@/shared/components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useMemberAddDialog } from '../hooks/use-member-add-dialog';
import { useAddMemberForm } from '../hooks/use-member-add-form';
import { useMemberAdd } from '../queries/member.mutations';
import { useConversationMembers } from '../queries/member.queries';
import { UserListSelected } from './user-list-selected';

export const MemberAddDialog: React.FC = () => {
  const { t } = useTranslation();
  const { show, conversationId, handleClose: handleDialogClose } = useMemberAddDialog();
  const { mutateAsync: addMembers, isPending } = useMemberAdd(conversationId);
  const { data: membersData, isLoading: isLoadingMembers } = useConversationMembers(conversationId);

  const {
    selectedUsers,
    setSelectedUsers,
    isSubmitting,
    setIsSubmitting,
    handleRemoveUser,
    handleReset,
    validateSelection
  } = useAddMemberForm();

  // Get existing member user IDs to filter them out
  const existingMemberIds = useMemo(() => {
    const memberIds = new Set<string>();

    // Add from membersData (current members in conversation)
    if (membersData) {
      membersData.forEach((member) => {
        if (member.user?.id) {
          memberIds.add(member.user.id);
        }
      });
    }

    return memberIds;
  }, [membersData]);

  const onValid = useCallback(async () => {
    if (!validateSelection()) {
      toast.error(t('conversations.validation.membersRequired'));
      return;
    }

    try {
      setIsSubmitting(true);

      const userIds = selectedUsers.map((user) => user.id);
      await addMembers(userIds);

      toast.success(t('conversations.toasts.addMemberSuccess'));
      handleReset();
      handleDialogClose(false, handleReset);
    } catch (error) {
      console.error('Failed to add members:', error);
      toast.error(t('conversations.toasts.addMemberError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [addMembers, selectedUsers, validateSelection, setIsSubmitting, handleReset, handleDialogClose]);

  const handleClose = useCallback(() => {
    handleDialogClose(selectedUsers.length > 0, handleReset);
  }, [selectedUsers.length, handleReset, handleDialogClose]);

  return (
    <Dialog open={show} fullWidth maxWidth='md' onClose={handleClose}>
      <DialogTitle variant='h6'>{t('conversations.members.addDialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack gap={3} flexDirection='column' sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box>
            <Typography variant='subtitle2' className='mex:!mb-2 mex:!font-medium'>
              {t('conversations.members.addButton')}
            </Typography>
            <UserAutocomplete
              selectedUsers={selectedUsers}
              onUserSelect={(user) => {
                if (existingMemberIds.has(user.id)) return;
                if (!selectedUsers.find((u) => u.id === user.id)) {
                  setSelectedUsers([...selectedUsers, user]);
                }
              }}
              placeholder={t('conversations.create.membersPlaceholder')}
              excludeUserIds={Array.from(existingMemberIds)}
              disabled={isPending || isSubmitting || isLoadingMembers}
            />
            {selectedUsers.length === 0 && (
              <Typography variant='caption' color='text.secondary' className='mex:!mt-1 mex:!block'>
                {t('conversations.validation.membersRequired')}
              </Typography>
            )}
          </Box>

          <Divider />

          <UserListSelected selects={selectedUsers} onRemove={handleRemoveUser} maxHeight={200} />
        </Stack>
      </DialogContent>

      <DialogActions className='mex:px-6 mex:pb-4'>
        <BackButton onClick={handleClose}>{t('conversations.create.cancelButton')}</BackButton>
        <Button
          variant='contained'
          color='primary'
          className='mex:!min-w-28'
          onClick={onValid}
          disabled={isPending || isSubmitting || selectedUsers.length === 0 || isLoadingMembers}
          loading={isPending || isSubmitting}
        >
          {isSubmitting ? t('conversations.members.adding') : t('conversations.members.addButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
